import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { cloneElement, createElement, isValidElement, useMemo } from 'react';
import { useLocation } from 'react-router';
import { isLogin as funcIsLogin, isGuest as funcIsGuest, isClient as funcIsClient, isAdmin as funcIsAdmin, isProfessional as funcIsProfessional, isFreeProfessional as funcIsFreeProfessional, isClientPreStatus, isProfessionalPreStatus, isCanUseOnlyProffitSearch as funcIsCanUseOnlyProffitSearch, isCanUseProffitSearch as funcIsCanUseProffitSearch, isCanUseClientProffit, isCanUseProfessionalProffitMart, isCanUseProfessionalProffit, isCanUseProfessionalSeminar as funcIsCanUseProfessionalSeminar, } from '@/helpers/auth';
import UserPreviewState from '@/helpers/user/UserPreviewState';
import { hasRequiredParam } from '@/helpers/user/UserProfileHelper';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import CustomRedirect from '@/route/CustomRedirect';
/**
 * ユーザーの権限やログイン状態に応じて、リダイレクトを行う
 * @constructor
 */
export const AuthenticationComponent = (props) => {
    const auth = useTypeSelector((state) => state.auth);
    const profile = useTypeSelector((state) => state.profile);
    const { pathname } = useLocation();
    const { children, isAdmin = null, isCanUseProfessionalSeminar = null, isCanUseProffit = null, isCanUseProffitMart = null, isCanUseProffitSearch = null, isClient = null, isFreeProfessional = null, isGuest = null, isMember = null, isNeed = null, isNotRequireCheckProfile = null, isPreClient = null, isPreProfessional = null, isPreview = null, isProfessional = null, notClientRedirectTo = '/login', notFreeProfessionalRedirectTo = '/login', notGuestRedirectTo = '/mypage', notIsAdminRedirectTo = '/mypage', notIsCanUseProfessionalSeminarRedirectTo = '/mypage', notIsCanUseProffitMartRedirectTo = '/mypage', notIsCanUseProffitRedirectTo = '/mypage', notIsCanUseProffitSearchRedirectTo = '/proffit_search/lp', notIsNeedRedirectTo = '/login', notMemberRedirectTo = '/login', notPreClientRedirectTo = '/login', notPreProfessionalRedirectTo = '/mypage', notProfessionalRedirectTo = '/login', } = props;
    const redirectSetting = useMemo(() => {
        // 必要な情報を所持しているかどうか判定
        // 利用可能な権限がSERVICE_PROFFIT_SEARCHだけの場合はプロフィールは必須ではないのでスキップ
        const isLoginState = funcIsLogin(auth);
        const isProfessionalState = funcIsProfessional(auth);
        const isClientState = funcIsClient(auth);
        const isClientPreState = isClientPreStatus(profile);
        const isProfessionalPreStatusState = isProfessionalPreStatus(profile);
        const isFreeProfessionalState = funcIsFreeProfessional(auth);
        const isCanUseOnlyProffitSearch = funcIsCanUseOnlyProffitSearch(profile);
        const isCanUseProffitSearchState = funcIsCanUseProffitSearch(profile);
        const isCanUseProfessionalSeminarState = isProfessionalState && funcIsCanUseProfessionalSeminar(profile);
        const isCanUseProffitMartState = (() => {
            if (isClientState) {
                return isCanUseClientProffit(profile);
            }
            if (isProfessionalState) {
                return isCanUseProfessionalProffitMart(profile);
            }
            return false;
        })();
        const isCanUseProffitState = (() => {
            if (isClientState) {
                return isCanUseClientProffit(profile);
            }
            if (isProfessionalState || isFreeProfessionalState) {
                return isCanUseProfessionalProffit(profile);
            }
            return false;
        })();
        const isGuestState = funcIsGuest(auth);
        const isAdminState = funcIsAdmin(profile);
        const hasRequiredParamState = hasRequiredParam(auth, profile);
        // プレビュー判定
        if (isPreview && UserPreviewState.isPreview) {
            return null;
        }
        // プロフィール必須入力判定チェック
        if (isLoginState &&
            !isCanUseOnlyProffitSearch &&
            !hasRequiredParamState &&
            !isNotRequireCheckProfile &&
            pathname !== '/mypage/profile') {
            return { redirect: '/mypage/profile' };
        }
        // コンサルの仮登録判定
        if (isPreProfessional !== null &&
            isProfessionalState &&
            isPreProfessional !== isProfessionalPreStatusState) {
            return { redirect: notPreProfessionalRedirectTo };
        }
        // コンサル判定
        if (isProfessional !== null && isProfessional !== isProfessionalState) {
            return { redirect: notProfessionalRedirectTo };
        }
        // フリーコンサル判定
        if (isFreeProfessional !== null &&
            isFreeProfessional !== isFreeProfessionalState) {
            return { redirect: notFreeProfessionalRedirectTo };
        }
        // 企業ユーザの仮登録判定
        if (isPreClient !== null &&
            isClientState &&
            isPreClient !== isClientPreState) {
            return { redirect: notPreClientRedirectTo };
        }
        // 企業ユーザ判定
        if (isClient !== null && isClient !== isClientState) {
            return { redirect: notClientRedirectTo };
        }
        // 会員判定
        if (isMember !== null && isMember !== isLoginState) {
            return { redirect: notMemberRedirectTo };
        }
        // ゲスト判定
        if (isGuest !== null && isGuest !== isGuestState) {
            return { redirect: notGuestRedirectTo };
        }
        // 管理者判定
        if (isAdmin !== null && isAdmin !== isAdminState) {
            return { redirect: notIsAdminRedirectTo };
        }
        // その他設定
        if (isNeed !== null && !isNeed) {
            return { redirect: notIsNeedRedirectTo };
        }
        // ProffitSearch利用可否判定
        if (isCanUseProffitSearch !== null &&
            isCanUseProffitSearch !== isCanUseProffitSearchState) {
            const { proffitSearch } = profile || {};
            const { isAccountDisable } = proffitSearch || {};
            return {
                message: isAccountDisable
                    ? 'ProffitSearchの使用が管理者に無効化されています'
                    : '',
                redirect: notIsCanUseProffitSearchRedirectTo,
            };
        }
        // Proffit利用可否判定
        if (isCanUseProffit !== null && isCanUseProffit !== isCanUseProffitState) {
            return { redirect: notIsCanUseProffitRedirectTo };
        }
        // ProffitMart利用可否判定
        if (isCanUseProffitMart !== null &&
            isCanUseProffitMart !== isCanUseProffitMartState) {
            return { redirect: notIsCanUseProffitMartRedirectTo };
        }
        // セミナー利用可否判定
        if (isCanUseProfessionalSeminar !== null &&
            isCanUseProfessionalSeminar !== isCanUseProfessionalSeminarState) {
            return { redirect: notIsCanUseProfessionalSeminarRedirectTo };
        }
        return null;
    }, [
        auth,
        profile,
        pathname,
        isPreview,
        isNotRequireCheckProfile,
        isPreProfessional,
        isProfessional,
        isFreeProfessional,
        isPreClient,
        isClient,
        isMember,
        isGuest,
        isAdmin,
        isNeed,
        isCanUseProffitSearch,
        isCanUseProffit,
        isCanUseProffitMart,
        isCanUseProfessionalSeminar,
        notPreProfessionalRedirectTo,
        notProfessionalRedirectTo,
        notFreeProfessionalRedirectTo,
        notPreClientRedirectTo,
        notClientRedirectTo,
        notMemberRedirectTo,
        notGuestRedirectTo,
        notIsAdminRedirectTo,
        notIsNeedRedirectTo,
        notIsCanUseProffitSearchRedirectTo,
        notIsCanUseProffitRedirectTo,
        notIsCanUseProffitMartRedirectTo,
        notIsCanUseProfessionalSeminarRedirectTo,
    ]);
    // リダイレクト条件に引っかかった時
    if (redirectSetting) {
        return (_jsx(CustomRedirect, { message: redirectSetting.message, to: redirectSetting.redirect }));
    }
    return isValidElement(children) ? cloneElement(children, { profile }) : null;
};
/**
 * ユーザーの権限やログイン状態に応じて、リダイレクトを行うHoc
 * @constructor
 * @deprecated hook化済: src/hooks/auth/useAuthentication.ts
 */
export const withAuthentication = (authProps) => (ChildComponent) => (props) => (_jsx(AuthenticationComponent, Object.assign({}, authProps, { children: createElement(ChildComponent, props) })));
